import React, { useEffect, useMemo, useCallback, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Divider,
    IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import ConfirmationPanel from '../confirmationPanel';

export type MethodFormValues = {
    name: string;
    summary: string;
    creator?: string;
};

export type MethodFormDialogProps = {
    open: boolean;
    initialValues?: MethodFormValues;
    title?: string;
    submitLabel?: string;
    onSubmit: (values: MethodFormValues) => void;
    onClose: () => void;
    defaultCreatorEmail?: string;
};

export const MethodFormDialog: React.FC<MethodFormDialogProps> = ({
    open,
    initialValues,
    title,
    submitLabel,
    onSubmit,
    onClose,
    defaultCreatorEmail,
}) => {
    const { t } = useTranslation();
    const resolvedTitle = title ?? t('coachingNavigator.methods.dialog.createTitle');
    const resolvedSubmitLabel = submitLabel ?? t('coachingNavigator.methods.dialog.create');
    const [values, setValues] = useState<MethodFormValues>({ name: '', summary: '' });
    const [warningDialogOpen, setWarningDialogOpen] = useState(false);

    const initialFormState = useMemo(() => ({
        name: initialValues?.name || '',
        summary: initialValues?.summary || '',
    }), [initialValues]);

    useEffect(() => {
        if (open) {
            setValues({
                name: initialValues?.name || '',
                summary: initialValues?.summary || '',
            });
        }
    }, [open, initialValues]);

    const hasChanges = useMemo(() => {
        return (
            values.name !== initialFormState.name ||
            values.summary !== initialFormState.summary
        );
    }, [values, initialFormState]);

    const handleWarningClose = useCallback(() => {
        if (hasChanges) {
            setWarningDialogOpen(true);
        } else {
            onClose();
        }
    }, [hasChanges, onClose]);

    const handleSubmit = () => {
        if (!values.name.trim()) return;
        onSubmit({
            name: values.name.trim(),
            summary: values.summary.trim(),
            creator: (defaultCreatorEmail || values.creator || '').trim() || undefined,
        });
    };

    return (
        <>
        <Dialog
            open={open}
            onClose={handleWarningClose}
            fullWidth
            maxWidth="sm" slotProps={{ paper: { sx: { backgroundColor: "white" } } }}
        >
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                {resolvedTitle}
                <IconButton onClick={handleWarningClose} size="small">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <Divider />
            <DialogContent sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                    label={t('coachingNavigator.methods.form.name')}
                    value={values.name}
                    onChange={(e) => setValues((prev) => ({ ...prev, name: e.target.value }))}
                    autoFocus
                    fullWidth
                ></TextField>
                <TextField
                    label={t('coachingNavigator.methods.form.summary')}
                    value={values.summary}
                    onChange={(e) => setValues((prev) => ({ ...prev, summary: e.target.value }))}
                    multiline
                    minRows={3}
                    fullWidth
                ></TextField>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button variant="outlined" onClick={handleWarningClose}>
                    {t('button.cancel')}
                </Button>
                <Button variant="contained" onClick={handleSubmit} disabled={!values.name.trim()}>
                    {resolvedSubmitLabel}
                </Button>
            </DialogActions>
        </Dialog>

        <ConfirmationPanel
            action="warning"
            showModal={warningDialogOpen}
            hideModal={() => setWarningDialogOpen(false)}
            confirmModal={() => {
                setWarningDialogOpen(false);
                setValues({ name: initialFormState.name, summary: initialFormState.summary });
                onClose();
            }}
        />
        </>
    );
};
